import { DEPARTMENT_RELATION_OPTIONS } from '../../DepartmentManager';

/**
 * How a person's relation to a department reads in Visão Geral.
 *
 * It was settable in Editar and invisible everywhere else: the department chips showed only the
 * department's name, so finding out whether somebody is the responsável or a regular funcionário
 * meant opening the editor — and opening an editor to READ something is how accidental edits
 * happen. The condomínio side has shown its relation as a pill on the unit card all along; this
 * is the same idea for the empresa side.
 *
 * Separate from the units' `formatRelationType`, deliberately. That one covers the residential
 * vocabulary (proprietario / inquilino / dependente / familiar); a department adds `responsavel`
 * and `funcionario` and has no dependente/familiar, so sharing the function would have printed a
 * raw "responsavel" at the operator.
 */

/** Shown when nobody has set the relation yet — never an empty pill. */
export const DEPARTMENT_RELATION_UNSET_LABEL = 'Tipo não definido';

/**
 * Driven off the editor's own option list so the two cannot drift: a value that becomes settable
 * in Editar is automatically readable here.
 */
const LABELS: Record<string, string> = Object.fromEntries(
  DEPARTMENT_RELATION_OPTIONS.map(({ value, label }) => [value, label]),
);

export const departmentRelationLabel = (
  relation: string | null | undefined,
): string => {
  const key = (relation ?? '').trim();
  if (!key) return DEPARTMENT_RELATION_UNSET_LABEL;
  // An unrecognised value is still information — probably written by an older client. Showing it
  // raw is honest; hiding it would make the chip claim the relation is unset when it is not.
  return LABELS[key] ?? key;
};

/**
 * The pill's colours.
 *
 * `responsavel` gets its own emphasis because it is the one relation that changes what you may
 * ask of the person — a department head should be findable by glancing down the list, not by
 * reading every chip. Everything else stays quiet so the department NAME remains the thing the
 * eye lands on.
 */
export const departmentRelationTone = (
  relation: string | null | undefined,
  darkMode: boolean,
): string => {
  const key = (relation ?? '').trim();
  const tones: Record<string, { dark: string; light: string }> = {
    responsavel:  { dark: 'bg-violet-900/40 text-violet-300',   light: 'bg-violet-50 text-violet-700' },
    proprietario: { dark: 'bg-indigo-900/40 text-indigo-300',   light: 'bg-indigo-50 text-indigo-700' },
    inquilino:    { dark: 'bg-amber-900/40 text-amber-300',     light: 'bg-amber-50 text-amber-700' },
    funcionario:  { dark: 'bg-slate-700/60 text-slate-300',     light: 'bg-slate-100 text-slate-600' },
  };
  const tone = tones[key] ?? {
    // Unset and unknown both read as "nothing asserted here", so they share the quietest tone.
    dark: 'bg-gray-700/50 text-gray-400',
    light: 'bg-gray-100 text-gray-500',
  };
  return darkMode ? tone.dark : tone.light;
};
